import { useAuthContext } from '@/auth/useAuthContext';
import { useConfirm } from '@/components/Confirm';
import { What3WordsInput } from '@/components/What3WordsInput';
import { createFacility, listFacilities, updateFacility } from '@/lib/api';
import { MapPin, PencilLine, Plus, X } from 'lucide-react';
import { useEffect, useState } from 'react';

interface FacilityDetail {
  id: string;
  accountId: string;
  slug: string;
  name: string;
  timezone: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  region: string | null;
  postcode: string | null;
  country: string | null;
  phone: string | null;
  email: string | null;
  what3words?: string | null;
  isActive: number;
}

interface FacilityFormState {
  name: string;
  slug: string;
  timezone: string;
  addressLine1: string;
  city: string;
  postcode: string;
  country: string;
  what3words: string;
}

const EMPTY_FORM: FacilityFormState = {
  name: '',
  slug: '',
  timezone: '',
  addressLine1: '',
  city: '',
  postcode: '',
  country: 'GB',
  what3words: '',
};

function formFromFacility(f: FacilityDetail): FacilityFormState {
  return {
    name: f.name,
    slug: f.slug,
    timezone: f.timezone ?? '',
    addressLine1: f.addressLine1 ?? '',
    city: f.city ?? '',
    postcode: f.postcode ?? '',
    country: f.country ?? 'GB',
    what3words: f.what3words ?? '',
  };
}

interface FacilityFormProps {
  initial?: FacilityFormState;
  isEdit?: boolean;
  onSave: (form: FacilityFormState) => Promise<void>;
  onCancel: () => void;
  saving: boolean;
}

function FacilityForm({ initial, isEdit, onSave, onCancel, saving }: FacilityFormProps) {
  const [form, setForm] = useState<FacilityFormState>(initial ?? EMPTY_FORM);

  function set(key: keyof FacilityFormState, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    await onSave(form);
  }

  const canSave = form.name.trim().length > 0 && (isEdit || form.slug.trim().length > 0);

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-4 rounded-lg border border-border bg-surface p-6"
    >
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2 space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Name *</label>
          <input
            className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            required
            placeholder="Main Facility"
          />
        </div>
        <div className="col-span-2 space-y-1">
          <label className="text-xs font-medium text-muted-foreground">
            Slug {isEdit ? '' : '*'}
          </label>
          {isEdit ? (
            <div className="flex items-center gap-2">
              <span className="rounded border border-border bg-muted px-2 py-1.5 text-sm text-muted-foreground">
                {form.slug}
              </span>
              <span className="text-xs text-muted-foreground">
                Slug cannot be changed after creation
              </span>
            </div>
          ) : (
            <input
              className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
              value={form.slug}
              onChange={(e) => set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              required
              placeholder="main-facility"
              pattern="[a-z0-9-]+"
              maxLength={60}
            />
          )}
        </div>
        <div className="col-span-2 space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Timezone</label>
          <input
            className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
            value={form.timezone}
            onChange={(e) => set('timezone', e.target.value)}
            placeholder="Europe/London"
          />
        </div>
        <div className="col-span-2 space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Address</label>
          <input
            className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
            value={form.addressLine1}
            onChange={(e) => set('addressLine1', e.target.value)}
            placeholder="123 High Street"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">City</label>
          <input
            className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
            value={form.city}
            onChange={(e) => set('city', e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Postcode</label>
          <input
            className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
            value={form.postcode}
            onChange={(e) => set('postcode', e.target.value)}
          />
        </div>
        <div className="col-span-2 space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Country</label>
          <input
            className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
            value={form.country}
            onChange={(e) => set('country', e.target.value)}
            placeholder="GB"
            maxLength={2}
          />
        </div>
        <div className="col-span-2 space-y-1">
          <label className="text-xs font-medium text-muted-foreground">
            what3words entrance (optional)
          </label>
          <What3WordsInput
            value={form.what3words}
            onChange={(v) => set('what3words', v)}
            country={form.country.trim() || null}
            aria-label="Facility what3words"
          />
          <p className="text-xs text-muted-foreground">
            The gate drivers leave from — used as the start point for transport distance.
          </p>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving || !canSave}
          className="rounded bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}

export function FacilitiesSettingsPage() {
  const { state } = useAuthContext();
  const caps = state.me?.capabilities ?? [];
  const editable = caps.includes('facilities.configure') || caps.includes('*');
  const { confirm, ConfirmDialog } = useConfirm();

  const [items, setItems] = useState<FacilityDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const data = await listFacilities();
      setItems((data.facilities ?? []) as FacilityDetail[]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visible = showAll ? items : items.filter((f) => f.isActive !== 0);

  async function handleAdd(form: FacilityFormState) {
    setSaving(true);
    try {
      await createFacility({
        name: form.name.trim(),
        slug: form.slug.trim(),
        timezone: form.timezone.trim() || null,
        city: form.city.trim() || null,
        country: form.country.trim() || null,
      });
      setAddOpen(false);
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function handleEdit(id: string, form: FacilityFormState) {
    setSaving(true);
    try {
      await updateFacility(id, {
        name: form.name.trim(),
        timezone: form.timezone.trim() || null,
        addressLine1: form.addressLine1.trim() || null,
        city: form.city.trim() || null,
        postcode: form.postcode.trim() || null,
        country: form.country.trim() || null,
        what3words: form.what3words.trim() || null,
      });
      setEditingId(null);
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDeactivate(id: string) {
    const yes = await confirm({
      title: 'Deactivate this facility?',
      message: 'The facility will be hidden from normal views. This can be reversed.',
      confirmLabel: 'Deactivate',
      danger: true,
    });
    if (!yes) return;
    try {
      await updateFacility(id, { isActive: 0 });
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <div className="space-y-6">
      <ConfirmDialog />
      <div>
        <h1 className="font-heading text-2xl font-medium tracking-tight">Facilities</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Manage the physical locations operated by this account.
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="mb-4 flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={showAll}
            onChange={(e) => setShowAll(e.target.checked)}
            className="rounded"
          />
          Show inactive
        </label>
        {editable && !addOpen && (
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="flex items-center gap-1 rounded bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
          >
            <Plus className="h-3.5 w-3.5" />
            Add facility
          </button>
        )}
      </div>

      {addOpen && editable && (
        <div className="mb-4">
          <FacilityForm onSave={handleAdd} onCancel={() => setAddOpen(false)} saving={saving} />
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : visible.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No facilities yet.{' '}
          {editable && !addOpen && (
            <button
              type="button"
              className="text-primary underline"
              onClick={() => setAddOpen(true)}
            >
              Add the first one.
            </button>
          )}
        </p>
      ) : (
        <div className="space-y-2">
          {visible.map((facility) => (
            <div
              key={facility.id}
              className={`rounded-lg border border-border bg-surface p-4 ${facility.isActive === 0 ? 'opacity-60' : ''}`}
            >
              {editingId === facility.id ? (
                <FacilityForm
                  initial={formFromFacility(facility)}
                  isEdit
                  onSave={(form) => handleEdit(facility.id, form)}
                  onCancel={() => setEditingId(null)}
                  saving={saving}
                />
              ) : (
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{facility.name}</span>
                      <span className="text-xs text-muted-foreground">/{facility.slug}</span>
                      {facility.isActive === 0 && (
                        <span className="rounded-full bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                          Inactive
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
                      {facility.timezone && <span>{facility.timezone}</span>}
                      {(facility.city || facility.postcode) && (
                        <span className="flex items-center gap-0.5">
                          <MapPin className="h-3 w-3" />
                          {[facility.city, facility.postcode].filter(Boolean).join(' ')}
                        </span>
                      )}
                    </div>
                  </div>
                  {editable && (
                    <div className="flex shrink-0 gap-1">
                      <button
                        type="button"
                        aria-label="Edit facility"
                        onClick={() => setEditingId(facility.id)}
                        className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                      >
                        <PencilLine className="h-3.5 w-3.5" />
                      </button>
                      {facility.isActive === 1 && (
                        <button
                          type="button"
                          aria-label="Deactivate facility"
                          onClick={() => handleDeactivate(facility.id)}
                          className="rounded p-1 text-muted-foreground hover:bg-red-50 hover:text-red-600"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
